import React from "react";
import { Group, Image as KImage, Rect, Text as KText } from "react-konva";
import type Konva from "konva";
import type { Filter as KonvaFilter } from "konva/lib/Node";
import { Blur } from "konva/lib/filters/Blur";
import { Brighten } from "konva/lib/filters/Brighten";
import { Contrast } from "konva/lib/filters/Contrast";
import { Grayscale } from "konva/lib/filters/Grayscale";
import { HSL } from "konva/lib/filters/HSL";
import { Invert } from "konva/lib/filters/Invert";
import { Sepia } from "konva/lib/filters/Sepia";
import type { Filter, PhotoAdjustments, FaceBlurSettings, LUTSettings } from "../types";
import { createHslCurvesFilter, createGrainFilter, createVignetteFilter, createLUTFilter } from "../utils/customFilters";
import { getSharedLutRenderer } from "../engine/VideoRegistry";
import { getHtmlImageStatus, getHtmlVideoStatus } from "../utils/htmlMedia";
import { studioDebug } from "../utils/studioDebug";
import type { KonvaNodeCommonProps } from "./CanvasStageTypes";
import type { VideoFilterPreviewQuality } from "./runtimePlaybackBudget";
import { markDirty, isRenderPlaying } from "../engine/RenderCoordinator";

function isFilterPerfDebugEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem("vb_debug_filter_perf") === "1";
}

interface FeatheredGroupProps {
  blurRadius: number;
  cacheKey: string;
  groupProps?: any;
  children: React.ReactNode;
  /** Ref to check if transform is in progress - skip cache updates during transform to prevent flash */
  isTransformingRef?: React.RefObject<boolean>;
}

export function FeatheredGroup({ blurRadius, cacheKey, groupProps, children, isTransformingRef }: FeatheredGroupProps) {
  const groupRef = React.useRef<Konva.Group>(null);

  React.useEffect(() => {
    const node = groupRef.current;
    if (!node) return;

    // Skip cache operations during transform to prevent resize flash
    if (isTransformingRef?.current) return;

    if (blurRadius > 0.001) {
      try {
        node.cache({ pixelRatio: 1 });
      } catch {}
      markDirty();
      return;
    }

    try {
      node.clearCache();
    } catch {}
    markDirty();
  }, [blurRadius, cacheKey, isTransformingRef]);

  // After transform ends, isTransformingRef.current flips to false but the effect above
  // won't re-run (ref identity is stable). Listen for the transform-end event instead
  // and rebuild the cache so the blurred group isn't stale after resize/rotate.
  React.useEffect(() => {
    const handleTransformEnd = () => {
      const node = groupRef.current;
      if (!node || blurRadius <= 0.001) return;
      try { node.cache({ pixelRatio: 1 }); } catch {}
      markDirty();
    };
    window.addEventListener("vb:canvas-transform-end", handleTransformEnd);
    return () => window.removeEventListener("vb:canvas-transform-end", handleTransformEnd);
  }, [blurRadius]);

  return (
    <Group
      ref={groupRef as any}
      {...groupProps}
      filters={blurRadius > 0.001 ? ([Blur] as any) : undefined}
      blurRadius={blurRadius}
      perfectDrawEnabled={false}
    >
      {children}
    </Group>
  );
}

// FilteredImage component - handles Konva filter caching with aggressive performance optimizations
interface FilteredImageProps {
  common: KonvaNodeCommonProps;
  width: number;
  height: number;
  image: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | ImageBitmap | undefined;
  /** Element id for export-time lookups. */
  elementId?: string;
  /** Original media source (video element or decoded canvas) for export-time effects. */
  sourceMedia?: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | null;
  /** Original HTMLVideoElement for audio capture during export. */
  sourceVideo?: HTMLVideoElement | null;
  /** Face blur settings (if enabled on the element). */
  faceBlur?: Partial<FaceBlurSettings>;
  filters: KonvaFilter[];
  filterProps: Record<string, number>;
  hasFilters: boolean;
  /** True when the element is a video, even if the source is a canvas. */
  isVideoElement?: boolean;
  /** Optional debug id for preview trace logging. */
  debugId?: string;
  crop?: { x: number; y: number; width: number; height: number };
  onDblClick?: () => void;
  /** Ref to check if transform is in progress - skip cache updates during transform to prevent flash */
  isTransformingRef?: React.RefObject<boolean>;
  /** GPU-accelerated filters (grain, vignette) - applied via CSS overlay */
  gpuFilters?: { grain?: number, vignette?: number };
  /** Stable filter signature for cache invalidation - prevents per-frame cache() calls */
  filterSignature?: string;
  /** LUT settings for video frame pump (pixel-level filter not expressible as CSS) */
  lutSettings?: LUTSettings;
  /** Editor-only filtered-video quality downgrade under playback pressure. */
  previewQuality?: VideoFilterPreviewQuality;
}

function buildCssFilterFromKonva(
  filters: KonvaFilter[],
  filterProps: Record<string, number>,
  previewQuality: VideoFilterPreviewQuality
): string {
  if (!filters || filters.length === 0) return "none";

  const parts: string[] = [];
  const has = (f: KonvaFilter) => filters.includes(f);

  if (has(Blur)) {
    const blurCap = previewQuality === "draft" ? 8 : previewQuality === "balanced" ? 12 : Number.POSITIVE_INFINITY;
    const blur = Math.min(Number(filterProps.blurRadius), blurCap);
    if (Number.isFinite(blur) && blur > 0) parts.push(`blur(${blur}px)`);
  }
  if (has(Brighten)) {
    const brightness = Number(filterProps.brightness);
    if (Number.isFinite(brightness)) parts.push(`brightness(${Math.max(0, 1 + brightness)})`);
  }
  if (has(Contrast)) {
    const contrast = Number(filterProps.contrast);
    if (Number.isFinite(contrast)) parts.push(`contrast(${Math.max(0, 1 + contrast / 100)})`);
  }
  if (has(HSL)) {
    const hue = Number(filterProps.hue);
    if (Number.isFinite(hue) && Math.abs(hue) > 0.001) parts.push(`hue-rotate(${hue}deg)`);
    const saturation = Number(filterProps.saturation);
    if (Number.isFinite(saturation) && Math.abs(saturation) > 0.001) {
      parts.push(`saturate(${Math.max(0, 1 + saturation)})`);
    }
  }
  if (has(Grayscale)) parts.push("grayscale(100%)");
  if (has(Sepia)) parts.push("sepia(100%)");
  if (has(Invert)) parts.push("invert(100%)");

  return parts.length > 0 ? parts.join(" ") : "none";
}

const GRAIN_PATTERN_CACHE = new Map<number, HTMLCanvasElement>();
const GRAIN_PATTERN_SIZE = 64;

function getGrainPattern(intensity: number): HTMLCanvasElement | null {
  if (typeof document === "undefined") return null;
  const key = Math.round(Math.max(0, Math.min(100, intensity)));
  if (key <= 0) return null;
  const cached = GRAIN_PATTERN_CACHE.get(key);
  if (cached) return cached;

  const canvas = document.createElement("canvas");
  canvas.width = GRAIN_PATTERN_SIZE;
  canvas.height = GRAIN_PATTERN_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const imageData = ctx.createImageData(GRAIN_PATTERN_SIZE, GRAIN_PATTERN_SIZE);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const v = Math.floor(Math.random() * 255);
    data[i] = v;
    data[i + 1] = v;
    data[i + 2] = v;
    data[i + 3] = 255;
  }
  ctx.putImageData(imageData, 0, 0);
  GRAIN_PATTERN_CACHE.set(key, canvas);
  return canvas;
}

type PreviewTraceEntry = {
  id: string;
  sourceType: "video" | "canvas";
  usingRVFC: boolean;
  pipeline: "framePump" | "konva";
  hasFilters: boolean;
  filterSignature?: string;
  framePumpBlocked?: boolean;
  samples: number[];
  lastFrameAt: number;
  lastMediaTime: number | null;
  lastVideoTime: number | null;
  readyState?: number;
  paused?: boolean;
  ended?: boolean;
};

type PreviewTraceFrameArgs = {
  id: string;
  now?: number;
  sourceType: "video" | "canvas";
  usingRVFC: boolean;
  pipeline: "framePump" | "konva";
  hasFilters: boolean;
  filterSignature?: string;
  framePumpBlocked?: boolean;
  mediaTime?: number;
  expectedDisplayTime?: number;
  video?: HTMLVideoElement;
};

const PREVIEW_TRACE = {
  enabled: false,
  entries: new Map<string, PreviewTraceEntry>(),
};

export function isPreviewTraceEnabled() {
  return PREVIEW_TRACE.enabled;
}

function ensurePreviewTraceEntry(id: string, init?: Partial<PreviewTraceEntry>): PreviewTraceEntry {
  let entry = PREVIEW_TRACE.entries.get(id);
  if (!entry) {
    entry = {
      id,
      sourceType: "video",
      usingRVFC: false,
      pipeline: "konva",
      hasFilters: false,
      samples: [],
      lastFrameAt: 0,
      lastMediaTime: null,
      lastVideoTime: null,
    };
    PREVIEW_TRACE.entries.set(id, entry);
  }
  if (init) Object.assign(entry, init);
  return entry;
}

function clearPreviewTraceEntry(id: string) {
  PREVIEW_TRACE.entries.delete(id);
}

function recordPreviewTraceFrame(args: PreviewTraceFrameArgs) {
  if (!PREVIEW_TRACE.enabled) return;
  const now = typeof args.now === "number" && Number.isFinite(args.now)
    ? args.now
    : (typeof performance !== "undefined" ? performance.now() : Date.now());
  const entry = ensurePreviewTraceEntry(args.id, {
    sourceType: args.sourceType,
    usingRVFC: args.usingRVFC,
    pipeline: args.pipeline,
    hasFilters: args.hasFilters,
    filterSignature: args.filterSignature,
    framePumpBlocked: args.framePumpBlocked,
  });

  if (entry.lastFrameAt > 0) {
    const delta = now - entry.lastFrameAt;
    if (Number.isFinite(delta) && delta > 0) {
      entry.samples.push(delta);
      if (entry.samples.length > 120) entry.samples.shift();
    }
  }
  entry.lastFrameAt = now;

  if (typeof args.mediaTime === "number") entry.lastMediaTime = args.mediaTime;
  if (args.video) {
    entry.lastVideoTime = Number.isFinite(args.video.currentTime) ? args.video.currentTime : entry.lastVideoTime;
    entry.readyState = args.video.readyState;
    entry.paused = args.video.paused;
    entry.ended = args.video.ended;
  }
}

function buildPreviewTraceSnapshot(now?: number) {
  const t = typeof now === "number" && Number.isFinite(now)
    ? now
    : (typeof performance !== "undefined" ? performance.now() : Date.now());

  const items = Array.from(PREVIEW_TRACE.entries.values()).map((entry) => {
    const samples = entry.samples;
    const count = samples.length;
    let min = 0;
    let max = 0;
    let avg = 0;
    let droppedGaps = 0;
    if (count > 0) {
      min = samples[0];
      max = samples[0];
      let sum = 0;
      for (const s of samples) {
        sum += s;
        if (s < min) min = s;
        if (s > max) max = s;
        if (s > 50) droppedGaps++;
      }
      avg = sum / count;
    }
    const fps = avg > 0 ? 1000 / avg : 0;
    const sinceLast = entry.lastFrameAt > 0 ? t - entry.lastFrameAt : 0;
    return {
      id: entry.id,
      sourceType: entry.sourceType,
      usingRVFC: entry.usingRVFC,
      pipeline: entry.pipeline,
      hasFilters: entry.hasFilters,
      filterSignature: entry.filterSignature,
      framePumpBlocked: entry.framePumpBlocked,
      fps: Number.isFinite(fps) ? Number(fps.toFixed(1)) : 0,
      avgMs: Number.isFinite(avg) ? Number(avg.toFixed(2)) : 0,
      jitterMs: Number.isFinite(max - min) ? Number((max - min).toFixed(2)) : 0,
      maxGapMs: Number.isFinite(max) ? Number(max.toFixed(2)) : 0,
      droppedGaps,
      sinceLastMs: Number.isFinite(sinceLast) ? Number(sinceLast.toFixed(2)) : 0,
      mediaTime: entry.lastMediaTime,
      videoTime: entry.lastVideoTime,
      readyState: entry.readyState,
      paused: entry.paused,
      ended: entry.ended,
    };
  });

  items.sort((a, b) => b.sinceLastMs - a.sinceLastMs);

  return {
    count: items.length,
    items,
  };
}

if (typeof window !== "undefined") {
  let enabled = false;
  try {
    const params = new URLSearchParams(window.location.search);
    enabled = window.localStorage.getItem("vb:previewTrace") === "1" || params.has("previewTrace");
  } catch {
    enabled = false;
  }
  PREVIEW_TRACE.enabled = enabled;
  if (enabled) {
    (window as any).__vbPreviewTrace = {
      enabled: true,
      entries: PREVIEW_TRACE.entries,
      snapshot: () => buildPreviewTraceSnapshot(),
    };
  }
}

export function FilteredImage({
  common,
  width,
  height,
  image,
  elementId,
  sourceMedia,
  sourceVideo,
  faceBlur,
  filters,
  filterProps,
  hasFilters,
  isVideoElement,
  debugId,
  crop,
  onDblClick,
  isTransformingRef,
  gpuFilters,
  filterSignature,
  lutSettings,
  previewQuality = "full",
}: FilteredImageProps) {
  // Track FilteredImage renders
  React.useEffect(() => {
    PERF_COUNTERS.filteredImageRenders++;
    PERF_COUNTERS.frameTimestamps.push(performance.now());
  });

  // LUT filter function ref — updated every render so frame pump always uses latest
  const lutFilterFnRef = React.useRef<((imageData: ImageData) => void) | null>(null);
  lutFilterFnRef.current = createLUTFilter(lutSettings);

  const imageRef = React.useRef<Konva.Image>(null);
  const cacheSigRef = React.useRef<string>("");
  const lastVideoCacheAtRef = React.useRef<number>(0);
  const lastVideoTimeRef = React.useRef<number>(-1);
  const cacheCountRef = React.useRef<number>(0);
  const videoFrameCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const videoFrameSourceRef = React.useRef<HTMLCanvasElement | null>(null);
  const videoFrameRafRef = React.useRef<number | null>(null);
  const videoFrameCallbackRef = React.useRef<number | null>(null);
  const framePumpBlockedRef = React.useRef<boolean>(false);
  const [renderMedia, setRenderMedia] = React.useState<HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | undefined>(undefined);
  const [mediaStatus, setMediaStatus] = React.useState<"missing" | "loading" | "error" | "ready">("missing");
  const [videoFrameSource, setVideoFrameSource] = React.useState<HTMLCanvasElement | null>(null);
  const [framePumpBlocked, setFramePumpBlocked] = React.useState(false);
  const lastFramePumpDrawMsRef = React.useRef<number>(0);
  // LUT renderer: shared singleton from VideoRegistry (reduces N WebGL2 contexts to 1)
  const webglLutRendererRef = React.useRef<ReturnType<typeof getSharedLutRenderer> | null>(null);

  const isVideoElementSource = !!isVideoElement;
  const shouldUseFramePump = isVideoElementSource && hasFilters && !framePumpBlocked;
  const cssFilter = React.useMemo(
    () => (shouldUseFramePump ? buildCssFilterFromKonva(filters, filterProps, previewQuality) : "none"),
    [shouldUseFramePump, filters, filterProps, previewQuality]
  );
  const useKonvaFilters = hasFilters && !shouldUseFramePump && !framePumpBlocked;
  const traceId = isPreviewTraceEnabled() && debugId ? debugId : null;

  React.useEffect(() => {
    if (!traceId) return;
    ensurePreviewTraceEntry(traceId);
    return () => clearPreviewTraceEntry(traceId);
  }, [traceId]);

  React.useEffect(() => {
    if (!traceId) return;
    ensurePreviewTraceEntry(traceId, {
      pipeline: shouldUseFramePump ? "framePump" : "konva",
      hasFilters,
      filterSignature,
      framePumpBlocked,
    });
  }, [traceId, shouldUseFramePump, hasFilters, filterSignature, framePumpBlocked]);

  // FIX: Track last applied state to prevent infinite re-render loops
  // when multiple videos/images are on the canvas simultaneously.
  const lastAppliedStatusRef = React.useRef<"missing" | "loading" | "error" | "ready">("missing");
  const lastAppliedMediaRef = React.useRef<HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | undefined>(undefined);
  const updatePendingRef = React.useRef<number | null>(null);

  // Gate drawing until media is truly drawable (prevents drawImage InvalidStateError).
  React.useEffect(() => {
    let cancelled = false;

    framePumpBlockedRef.current = false;
    setFramePumpBlocked(false);

    const update = () => {
      if (cancelled) return;

      // FIX: Clear any pending update to prevent duplicate calls
      if (updatePendingRef.current !== null) {
        cancelAnimationFrame(updatePendingRef.current);
        updatePendingRef.current = null;
      }

      let nextStatus: "missing" | "loading" | "error" | "ready" = "missing";
      let nextMedia: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | undefined = undefined;

      if (!image) {
        nextStatus = "missing";
        nextMedia = undefined;
      } else {
        const isImg = typeof HTMLImageElement !== "undefined" && image instanceof HTMLImageElement;
        if (isImg) {
          const st = getHtmlImageStatus(image);
          nextStatus = st;
          // FIX: Keep rendering image even when loading to prevent flash
          // Only hide if truly missing or error
          nextMedia = (st === "ready" || st === "loading") ? image : undefined;
        } else {
          const isVid = typeof HTMLVideoElement !== "undefined" && image instanceof HTMLVideoElement;
          if (isVid) {
            const st = getHtmlVideoStatus(image);
            nextStatus = st;
            // FIX: Keep rendering video even when readyState drops below 2
            // The browser will draw whatever frame is available, preventing flash
            // at clip boundaries. Only hide if truly missing or error.
            nextMedia = (st === "ready" || st === "loading") ? image : undefined;
          } else {
            const isCanvas = typeof HTMLCanvasElement !== "undefined" && image instanceof HTMLCanvasElement;
            if (isCanvas) {
              const ok = image.width > 0 && image.height > 0;
              nextStatus = ok ? "ready" : "loading";
              // FIX: Keep canvas in renderMedia during loading to prevent flash
              nextMedia = image;
            }
          }
        }
      }

      // FIX: Only update state if values actually changed
      // This prevents infinite re-render loops when multiple media elements
      // fire events simultaneously.
      const statusChanged = nextStatus !== lastAppliedStatusRef.current;
      const mediaChanged = nextMedia !== lastAppliedMediaRef.current;

      if (statusChanged || mediaChanged) {
        lastAppliedStatusRef.current = nextStatus;
        lastAppliedMediaRef.current = nextMedia;
        setMediaStatus(nextStatus);
        setRenderMedia(nextMedia);
      }
    };

    update();

    if (typeof HTMLImageElement !== "undefined" && image instanceof HTMLImageElement) {
      const onLoad = () => update();
      const onError = () => update();
      image.addEventListener("load", onLoad);
      image.addEventListener("error", onError);
      return () => {
        cancelled = true;
        if (updatePendingRef.current !== null) cancelAnimationFrame(updatePendingRef.current);
        image.removeEventListener("load", onLoad);
        image.removeEventListener("error", onError);
      };
    }

    if (typeof HTMLVideoElement !== "undefined" && image instanceof HTMLVideoElement) {
      // FIX: Debounce video event handlers to prevent rapid consecutive updates
      const onReady = () => {
        if (updatePendingRef.current !== null) cancelAnimationFrame(updatePendingRef.current);
        updatePendingRef.current = requestAnimationFrame(update);
      };
      const onError = () => update();
      image.addEventListener("loadeddata", onReady);
      image.addEventListener("canplay", onReady);
      image.addEventListener("error", onError);
      return () => {
        cancelled = true;
        if (updatePendingRef.current !== null) cancelAnimationFrame(updatePendingRef.current);
        image.removeEventListener("loadeddata", onReady);
        image.removeEventListener("canplay", onReady);
        image.removeEventListener("error", onError);
      };
    }

    return () => {
      cancelled = true;
      if (updatePendingRef.current !== null) cancelAnimationFrame(updatePendingRef.current);
    };
  }, [image]);

  // Video frame pump: use requestVideoFrameCallback (or RAF fallback) to keep frames moving under filters.
  React.useEffect(() => {
    const source = renderMedia;

    if (!shouldUseFramePump || !isVideoElementSource) {
      if (videoFrameSourceRef.current) {
        videoFrameSourceRef.current = null;
        setVideoFrameSource(null);
      }
      return;
    }

    if (!renderMedia || mediaStatus === "missing" || mediaStatus === "error") {
      if (videoFrameSourceRef.current) {
        videoFrameSourceRef.current = null;
        setVideoFrameSource(null);
      }
      return;
    }

    if (!source) return;

    const isVideo =
      typeof HTMLVideoElement !== "undefined" && source instanceof HTMLVideoElement;
    const isCanvas =
      typeof HTMLCanvasElement !== "undefined" && source instanceof HTMLCanvasElement;

    if (!isVideo && !isCanvas) return;

    const node = imageRef.current;
    if (!node) return;

    const canvas = videoFrameCanvasRef.current ?? document.createElement("canvas");
    videoFrameCanvasRef.current = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (videoFrameSourceRef.current !== canvas) {
      videoFrameSourceRef.current = canvas;
      setVideoFrameSource(canvas);
    }

    let cancelled = false;

    const rVFC = isVideo
      ? ((source as HTMLVideoElement).requestVideoFrameCallback?.bind(source) as undefined | ((cb: any) => number))
      : undefined;
    const cancelRvfC = isVideo
      ? ((source as HTMLVideoElement).cancelVideoFrameCallback?.bind(source) as undefined | ((id: number) => void))
      : undefined;
    const usingRVFC = !!rVFC;

    const previewRenderScale =
      previewQuality === "draft" ? 0.5 : previewQuality === "balanced" ? 0.75 : 1;
    const targetFrameIntervalMs =
      previewQuality === "draft" ? 1000 / 12 : previewQuality === "balanced" ? 1000 / 24 : 0;

    let lastDrawnVideoTime = -1;

    const drawFrame = () => {
      if (isVideo && (source as HTMLVideoElement).readyState < 2) return;

      // Skip draw if video frame hasn't changed (same currentTime = same decoded frame)
      if (isVideo) {
        const currentVideoTime = (source as HTMLVideoElement).currentTime;
        if (currentVideoTime === lastDrawnVideoTime) return;
        lastDrawnVideoTime = currentVideoTime;
      }

      const vw = isVideo
        ? (source as HTMLVideoElement).videoWidth
        : (source as HTMLCanvasElement).width;
      const vh = isVideo
        ? (source as HTMLVideoElement).videoHeight
        : (source as HTMLCanvasElement).height;
      if (!vw || !vh) return;

      const targetCanvasWidth = Math.max(1, Math.round(vw * previewRenderScale));
      const targetCanvasHeight = Math.max(1, Math.round(vh * previewRenderScale));
      if (canvas.width !== targetCanvasWidth || canvas.height !== targetCanvasHeight) {
        canvas.width = targetCanvasWidth;
        canvas.height = targetCanvasHeight;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality =
        previewQuality === "draft" ? "low" : previewQuality === "balanced" ? "medium" : "high";
      ctx.filter = cssFilter && cssFilter !== "none" ? cssFilter : "none";
      try {
        ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      } catch (err) {
        if (!framePumpBlockedRef.current && err && typeof err === "object") {
          const name = (err as any).name;
          if (name === "SecurityError") {
            framePumpBlockedRef.current = true;
            setFramePumpBlocked(true);
            if (traceId) {
              ensurePreviewTraceEntry(traceId, { framePumpBlocked: true });
            }
            if (videoFrameSourceRef.current) {
              videoFrameSourceRef.current = null;
              setVideoFrameSource(null);
            }
          }
        }
      }

      // Apply LUT color grading — GPU path (WebGL2 3D texture, all quality tiers)
      // with CPU fallback (getImageData/putImageData, full quality only)
      if (lutSettings?.enabled && lutSettings.lut && lutSettings.intensity > 0) {
        try {
          if (!webglLutRendererRef.current) {
            webglLutRendererRef.current = getSharedLutRenderer();
          }
          const lutRenderer = webglLutRendererRef.current;
          lutRenderer.uploadLut(lutSettings.lut);
          const lutResult = lutRenderer.render(canvas, lutSettings.intensity, lutSettings.lut.size);
          if (lutResult) {
            ctx.filter = "none";
            ctx.drawImage(lutResult, 0, 0, canvas.width, canvas.height);
          } else if (lutFilterFnRef.current && previewQuality === "full") {
            ctx.filter = "none";
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            lutFilterFnRef.current(imgData);
            ctx.putImageData(imgData, 0, 0);
          }
        } catch {
          // Ignore cross-origin or WebGL errors
        }
      }
    };

    const pumpFrame = (frameNow?: number, metadata?: any) => {
      if (cancelled) return;
      // During playback, RenderCoordinator Phase 3/4 handles frame capture + effects.
      // This pump only runs when paused/scrubbing for immediate visual feedback.
      if (isRenderPlaying()) return;
      const effectiveNow =
        typeof frameNow === "number" && Number.isFinite(frameNow) ? frameNow : performance.now();
      if (
        targetFrameIntervalMs > 0 &&
        effectiveNow - lastFramePumpDrawMsRef.current < targetFrameIntervalMs
      ) {
        return;
      }
      lastFramePumpDrawMsRef.current = effectiveNow;
      const start = performance.now();
      drawFrame();
      PERF_COUNTERS.videoFrameUpdates++;
      PERF_COUNTERS.renderTicks++;
      const duration = performance.now() - start;
      PERF_COUNTERS.renderTickDurations.push(duration);
      markDirty();

      if (traceId) {
        recordPreviewTraceFrame({
          id: traceId,
          now: frameNow,
          sourceType: isVideo ? "video" : "canvas",
          usingRVFC,
          pipeline: "framePump",
          hasFilters,
          filterSignature,
          framePumpBlocked,
          mediaTime: metadata?.mediaTime,
          expectedDisplayTime: metadata?.expectedDisplayTime,
          video: isVideo ? (source as HTMLVideoElement) : undefined,
        });
      }
    };

    if (rVFC) {
      const onFrame = (now?: number, metadata?: any) => {
        pumpFrame(now, metadata);
        if (!cancelled) {
          videoFrameCallbackRef.current = rVFC(onFrame);
        }
      };
      videoFrameCallbackRef.current = rVFC(onFrame);
    } else {
      const tick = () => {
        pumpFrame();
        if (!cancelled) {
          videoFrameRafRef.current = requestAnimationFrame(tick);
        }
      };
      videoFrameRafRef.current = requestAnimationFrame(tick);
    }

    pumpFrame();

    const onSeeked = () => pumpFrame();
    if (isVideo) {
      (source as HTMLVideoElement).addEventListener("seeked", onSeeked);
      (source as HTMLVideoElement).addEventListener("loadeddata", onSeeked);
      (source as HTMLVideoElement).addEventListener("pause", onSeeked);
    }

    return () => {
      cancelled = true;
      if (videoFrameCallbackRef.current !== null && cancelRvfC) {
        cancelRvfC(videoFrameCallbackRef.current);
      }
      if (videoFrameRafRef.current !== null) {
        cancelAnimationFrame(videoFrameRafRef.current);
      }
      videoFrameCallbackRef.current = null;
      videoFrameRafRef.current = null;
      if (isVideo) {
        (source as HTMLVideoElement).removeEventListener("seeked", onSeeked);
        (source as HTMLVideoElement).removeEventListener("loadeddata", onSeeked);
        (source as HTMLVideoElement).removeEventListener("pause", onSeeked);
      }
      // Release ref to shared LUT renderer (singleton — NOT disposed here, shared across all videos)
      webglLutRendererRef.current = null;
    };
  }, [
    cssFilter,
    filterSignature,
    framePumpBlocked,
    hasFilters,
    isVideoElementSource,
    mediaStatus,
    previewQuality,
    renderMedia,
    shouldUseFramePump,
    traceId,
    lutSettings?.enabled,
    lutSettings?.intensity,
    lutSettings?.lut,
  ]);

  // EFFECT 1: Cache the node when filter parameters change
  // This is the PRIMARY caching mechanism - only re-cache when filters actually change
  React.useEffect(() => {
    const node = imageRef.current;
    if (!node || !renderMedia) return;

    // Skip cache operations during transform to prevent resize flash
    if (isTransformingRef?.current) return;

    const isVideo = typeof HTMLVideoElement !== "undefined" && renderMedia instanceof HTMLVideoElement;

    if (!useKonvaFilters) {
      // Clear cache when Konva filters are disabled (video frame pump uses CSS filters).
      try {
        node.clearCache();
      } catch {
        // Ignore clear cache errors
      }
      const drawStart = performance.now();
      markDirty();
      const drawDuration = performance.now() - drawStart;
      PERF_COUNTERS.batchDrawCalls++;
      PERF_COUNTERS.drawDurations.push(drawDuration);

      if (drawDuration > 50) {
        PERF_COUNTERS.longTasks++;
      }
      return;
    }

    // Use stable filterSignature from parent to prevent per-frame cache() calls
    // Fallback to local signature if not provided (backwards compatibility)
    const nextSig = filterSignature ?? [
      useKonvaFilters ? "1" : "0",
      isVideo ? "v" : "i",
      crop ? `crop:${crop.x},${crop.y},${crop.width},${crop.height}` : "crop:none",
      ...(useKonvaFilters ? filters.map((f) => (f as any)?.name ?? "anon") : ["none"]),
      ...Object.entries(filterProps)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${String(v)}`),
    ].join("|");

    // Only re-cache if signature changed (filter params changed)
    if (nextSig === cacheSigRef.current) return;
    cacheSigRef.current = nextSig;

    if (isFilterPerfDebugEnabled()) {
      studioDebug(`[FilterCache] Effect #1: Filter params changed, re-caching ${isVideo ? 'video' : 'image'}`, { sig: nextSig });
    }

    if (useKonvaFilters) {
      // Cache the node for filters to work
      // Use pixelRatio 1 for full quality (was 0.75 for video which caused blurriness)
      performance.mark('cache-start');
      const cacheStart = performance.now();
      try {
        node.cache({ pixelRatio: 1 });
        cacheCountRef.current++;
        PERF_COUNTERS.cacheOperations++;

        const cacheDuration = performance.now() - cacheStart;
        PERF_COUNTERS.cacheDurations.push(cacheDuration);

        // Track long tasks (> 50ms)
        if (cacheDuration > 50) {
          PERF_COUNTERS.longTasks++;
          if (isFilterPerfDebugEnabled()) {
            console.warn(`[FilterPerf] cache() took ${cacheDuration.toFixed(2)}ms`);
          }
        }

        performance.mark('cache-end');
        performance.measure('FilteredImage:cache', 'cache-start', 'cache-end');

        if (isFilterPerfDebugEnabled()) {
          studioDebug(`[FilterCache] Effect #1: Cache #${cacheCountRef.current} complete (${cacheDuration.toFixed(2)}ms)`);
        }
      } catch {
        // Failed to cache image for filters - ignore
      }

      performance.mark('batchDraw-start');
      const drawStart = performance.now();
      markDirty();
      const drawDuration = performance.now() - drawStart;
      PERF_COUNTERS.batchDrawCalls++;
      PERF_COUNTERS.drawDurations.push(drawDuration);

      if (drawDuration > 50) {
        PERF_COUNTERS.longTasks++;
        if (isFilterPerfDebugEnabled()) {
          console.warn(`[FilterPerf] batchDraw() took ${drawDuration.toFixed(2)}ms`);
        }
      }

      performance.mark('batchDraw-end');
      performance.measure('FilteredImage:batchDraw', 'batchDraw-start', 'batchDraw-end');
    }
  }, [filterSignature, filterProps, filters, renderMedia, crop, isTransformingRef, useKonvaFilters]);

  // EFFECT 2b: Rebuild Konva cache after transform ends.
  // Effect 1 skips cache() while isTransformingRef.current is true, but React won't re-run
  // Effect 1 when only .current changes (ref identity is stable). The transformer dispatches
  // "vb:canvas-transform-end" after 2 RAFs — listen here and rebuild so the node isn't stale.
  React.useEffect(() => {
    const handleTransformEnd = () => {
      const node = imageRef.current;
      if (!node) return;
      cacheSigRef.current = ""; // force Effect 1 to re-cache on next prop change
      // Directly rebuild now so the result is immediately visible
      if ((node as any).isCached?.()) {
        try { node.cache({ pixelRatio: 1 }); } catch {}
        markDirty();
      }
    };
    window.addEventListener("vb:canvas-transform-end", handleTransformEnd);
    return () => window.removeEventListener("vb:canvas-transform-end", handleTransformEnd);
  }, []); // uses refs only — no dep needed

  // EFFECT 2: REMOVED - was causing redundant caching with 50ms setTimeout
  // The primary filter signature effect (#1) handles all filter parameter changes
  // Video-specific updates are handled by effect #3 below

  // EFFECT 3: Video-specific intelligent re-caching
  // Strategy: Avoid cache() during smooth playback to prevent FPS drops
  // Only cache on: seeks, paused scrubbing, or very infrequent updates
  React.useEffect(() => {
    const node = imageRef.current;
    const v = renderMedia;
    if (!node || !v) return;
    const isVideo = typeof HTMLVideoElement !== "undefined" && v instanceof HTMLVideoElement;
    if (!isVideo) return;
    if (!useKonvaFilters) return;

    // Performance tuning constants
    const SEEK_THRESHOLD = 0.5; // Time jump > 500ms is definitely a seek
    const PLAYBACK_CACHE_INTERVAL = 1000; // During playback, cache at most once per second
    const SCRUB_THRESHOLD = 0.05; // Small time changes when paused (scrubbing)

    let disposed = false;

    const tryCache = (reason: string, timeDiff: number, timeSinceLastCache: number) => {
      if (disposed) return;
      // Skip cache operations during transform to prevent resize flash
      if (isTransformingRef?.current) return;

      const now = performance.now();
      lastVideoCacheAtRef.current = now;
      lastVideoTimeRef.current = v.currentTime;

      if (isFilterPerfDebugEnabled()) {
        studioDebug(`[FilterCache] Effect #3 (event): Video re-cache triggered`, {
          reason,
          timeDiff: Number.isFinite(timeDiff) ? timeDiff.toFixed(3) : String(timeDiff),
          timeSinceLastCache: Number.isFinite(timeSinceLastCache) ? timeSinceLastCache.toFixed(0) : String(timeSinceLastCache),
          paused: v.paused,
        });
      }

      try {
        // Use pixelRatio 1 for full quality video rendering
        node.cache({ pixelRatio: 1 });
        cacheCountRef.current++;
        markDirty();
      } catch {
        // Ignore transient cache errors
      }
    };

    const onSeeked = () => {
      const now = performance.now();
      const t = v.currentTime;
      const prevT = lastVideoTimeRef.current;
      const timeDiff = prevT >= 0 && Number.isFinite(t) ? Math.abs(t - prevT) : Infinity;
      const timeSinceLastCache = now - lastVideoCacheAtRef.current;
      // Seeked is an explicit seek/scrub event; always allow cache.
      tryCache("SEEKED", timeDiff, timeSinceLastCache);
    };

    const onTimeUpdate = () => {
      performance.mark('videoFrameUpdate-start');
      PERF_COUNTERS.videoFrameUpdates++;
      PERF_COUNTERS.renderTicks++;

      const now = performance.now();
      const t = v.currentTime;
      const prevT = lastVideoTimeRef.current;
      if (!Number.isFinite(t)) return;

      if (prevT < 0) {
        lastVideoTimeRef.current = t;
        performance.mark('videoFrameUpdate-end');
        performance.measure('video:timeupdate', 'videoFrameUpdate-start', 'videoFrameUpdate-end');
        return;
      }

      const timeDiff = Math.abs(t - prevT);
      const timeSinceLastCache = now - lastVideoCacheAtRef.current;

      // Detect major seeks that don't reliably fire `seeked` (best-effort).
      const isSeek = timeDiff > SEEK_THRESHOLD;

      // Allow infrequent periodic updates during playback (max 1 FPS).
      const allowPeriodicUpdate =
        !v.paused && timeDiff > 0.02 && timeSinceLastCache >= PLAYBACK_CACHE_INTERVAL;

      if (isSeek || allowPeriodicUpdate) {
        tryCache(isSeek ? "TIMEUPDATE_SEEK" : "TIMEUPDATE_PERIODIC", timeDiff, timeSinceLastCache);
        performance.mark('videoFrameUpdate-end');
        performance.measure('video:timeupdate', 'videoFrameUpdate-start', 'videoFrameUpdate-end');
        return;
      }

      // Keep tracking time without recaching (smooth playback).
      if (timeDiff > 0.001) lastVideoTimeRef.current = t;

      const timeupdateDuration = performance.now() - now;
      PERF_COUNTERS.renderTickDurations.push(timeupdateDuration);

      if (timeupdateDuration > 16.67) { // >16.67ms = dropped frame at 60fps
        if (isFilterPerfDebugEnabled()) {
          console.warn(`[FilterPerf] timeupdate took ${timeupdateDuration.toFixed(2)}ms`);
        }
      }

      performance.mark('videoFrameUpdate-end');
      performance.measure('video:timeupdate', 'videoFrameUpdate-start', 'videoFrameUpdate-end');
    };

    const onPause = () => {
      const now = performance.now();
      const t = v.currentTime;
      const prevT = lastVideoTimeRef.current;
      if (!Number.isFinite(t) || prevT < 0) {
        if (Number.isFinite(t)) lastVideoTimeRef.current = t;
        return;
      }
      const timeDiff = Math.abs(t - prevT);
      const timeSinceLastCache = now - lastVideoCacheAtRef.current;
      // If the user scrubbed while paused, cache once on pause boundary.
      if (timeDiff > SCRUB_THRESHOLD) {
        tryCache("PAUSE_SCRUB", timeDiff, timeSinceLastCache);
      }
    };

    // Initialize time tracking.
    if (Number.isFinite(v.currentTime)) lastVideoTimeRef.current = v.currentTime;

    v.addEventListener("seeked", onSeeked);
    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("pause", onPause);

    return () => {
      disposed = true;
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("pause", onPause);

      if (isFilterPerfDebugEnabled()) {
        studioDebug(`[FilterCache] Effect #3 cleanup, total caches: ${cacheCountRef.current}`);
      }
    };
  }, [renderMedia, useKonvaFilters]);

  const grainIntensity = gpuFilters?.grain ?? 0;
  const vignetteIntensity = gpuFilters?.vignette ?? 0;
  const grainPattern = React.useMemo(() => getGrainPattern(grainIntensity), [grainIntensity]);
  const grainOpacity = Math.min(1, (Math.max(0, Math.min(100, grainIntensity)) / 100) * 0.25);
  const vignetteNormalized = Math.max(0, Math.min(100, vignetteIntensity)) / 100;
  const vignetteMaxOpacity = vignetteNormalized * 0.8;
  const vignetteRadius = Math.hypot(width, height) / 2;
  const { globalCompositeOperation, ...groupProps } = common;

  // FIX: Use last known good media as fallback during brief transitions
  // This prevents flash/disappearance when renderMedia temporarily becomes undefined
  // (e.g., during filter changes, video seeking, or clip boundary transitions)
  const effectiveMedia = renderMedia ?? lastAppliedMediaRef.current;

  // Only show placeholder when media is truly missing or has error and no fallback exists
  if (mediaStatus === "missing" || mediaStatus === "error" || !effectiveMedia) {
    const label =
      mediaStatus === "loading"
        ? "Loading…"
        : mediaStatus === "error"
          ? "Media unavailable"
          : "";

    return (
      <Group {...common} width={width} height={height} onDblClick={onDblClick}>
        <Rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="rgba(0,0,0,0.5)"
          stroke="rgba(0,0,0,0.18)"
          strokeWidth={1}
          dash={[6, 4]}
        />
        {label ? (
          <KText
            x={0}
            y={0}
            width={width}
            height={height}
            text={label}
            fontSize={14}
            fill="rgba(255,255,255,0.65)"
            align="center"
            verticalAlign="middle"
            listening={false}
          />
        ) : null}
      </Group>
    );
  }

  // GPU filters (grain, vignette) are rendered via lightweight Konva overlays.

  return (
    <Group {...groupProps} globalCompositeOperation={globalCompositeOperation}>
      <KImage
        ref={imageRef}
        x={0}
        y={0}
        width={width}
        height={height}
        image={videoFrameSource ?? effectiveMedia}
        vbElementId={elementId}
        vbSource={sourceMedia || undefined}
        vbVideo={sourceVideo || undefined}
        vbFaceBlur={faceBlur || undefined}
        cropX={crop ? crop.x : undefined}
        cropY={crop ? crop.y : undefined}
        cropWidth={crop ? crop.width : undefined}
        cropHeight={crop ? crop.height : undefined}
        filters={useKonvaFilters ? filters : undefined}
        {...(useKonvaFilters ? filterProps : {})}
        perfectDrawEnabled={false}
        onDblClick={onDblClick}
      />
      {grainPattern && grainOpacity > 0.001 && (
        <Rect
          x={0}
          y={0}
          width={width}
          height={height}
          fillPatternImage={grainPattern as unknown as HTMLImageElement}
          fillPatternRepeat="repeat"
          opacity={grainOpacity}
          globalCompositeOperation="overlay"
          listening={false}
        />
      )}
      {vignetteMaxOpacity > 0.001 && (
        <Rect
          x={0}
          y={0}
          width={width}
          height={height}
          fillRadialGradientStartPoint={{ x: width / 2, y: height / 2 }}
          fillRadialGradientEndPoint={{ x: width / 2, y: height / 2 }}
          fillRadialGradientStartRadius={0}
          fillRadialGradientEndRadius={vignetteRadius}
          fillRadialGradientColorStops={[
            0, "rgba(0,0,0,0)",
            0.3, "rgba(0,0,0,0)",
            1, `rgba(0,0,0,${vignetteMaxOpacity})`,
          ]}
          globalCompositeOperation="multiply"
          listening={false}
        />
      )}
    </Group>
  );
}

// Performance instrumentation - COMPREHENSIVE TRACE PACK
// DISABLED by default. Enable via: localStorage.setItem('vb_perf_hud', '1')
const PERF_HUD_ENABLED = typeof window !== 'undefined' && localStorage.getItem('vb_perf_hud') === '1';

const PERF_COUNTERS = {
  buildKonvaFilters: 0,
  cacheOperations: 0,
  batchDrawCalls: 0,
  cacheDurations: [] as number[],
  renderTicks: 0,
  filteredImageRenders: 0,
  filterComputeDurations: [] as number[],
  drawDurations: [] as number[],
  renderTickDurations: [] as number[],
  videoFrameUpdates: 0,
  longTasks: 0, // Tasks > 50ms
  lastResetTime: performance.now(),
  frameTimestamps: [] as number[],
};

// FPS calculation
let lastFrameTime = performance.now();
let frameCount = 0;
let currentFPS = 0;

// Log performance snapshot every second - ENHANCED HUD
// Only runs when PERF_HUD_ENABLED is true (localStorage 'vb_perf_hud' === '1')
let perfHudIntervalId: ReturnType<typeof setInterval> | null = null;
if (PERF_HUD_ENABLED) {
  perfHudIntervalId = setInterval(() => {
    const elapsed = (performance.now() - PERF_COUNTERS.lastResetTime) / 1000;
    if (elapsed > 0) {
      const avgCacheDuration = PERF_COUNTERS.cacheDurations.length > 0
        ? PERF_COUNTERS.cacheDurations.reduce((a, b) => a + b, 0) / PERF_COUNTERS.cacheDurations.length
        : 0;

      const avgFilterComputeDuration = PERF_COUNTERS.filterComputeDurations.length > 0
        ? PERF_COUNTERS.filterComputeDurations.reduce((a, b) => a + b, 0) / PERF_COUNTERS.filterComputeDurations.length
        : 0;

      const avgDrawDuration = PERF_COUNTERS.drawDurations.length > 0
        ? PERF_COUNTERS.drawDurations.reduce((a, b) => a + b, 0) / PERF_COUNTERS.drawDurations.length
        : 0;

      const avgRenderTickMs = PERF_COUNTERS.renderTickDurations.length > 0
        ? PERF_COUNTERS.renderTickDurations.reduce((a, b) => a + b, 0) / PERF_COUNTERS.renderTickDurations.length
        : 0;

      // Calculate actual FPS from frame timestamps
      const frames = PERF_COUNTERS.frameTimestamps;
      const actualFPS = frames.length > 1
        ? (frames.length - 1) / ((frames[frames.length - 1] - frames[0]) / 1000)
        : 0;

      // Calculate dropped frames (expected 60 FPS)
      const expectedFrames = elapsed * 60;
      const droppedFrames = Math.max(0, expectedFrames - frames.length);

      studioDebug('[🎯 PERF HUD]', {
        fps: actualFPS.toFixed(1),
        droppedFrames: droppedFrames.toFixed(0),
        longTasks: PERF_COUNTERS.longTasks,
        drawCalls: PERF_COUNTERS.batchDrawCalls,
        cacheCalls: PERF_COUNTERS.cacheOperations,
        filterRebuilds: PERF_COUNTERS.buildKonvaFilters,
        avgRenderTickMs: avgRenderTickMs.toFixed(2),
        avgDrawMs: avgDrawDuration.toFixed(2),
        avgCacheMs: avgCacheDuration.toFixed(2),
        avgFilterComputeMs: avgFilterComputeDuration.toFixed(2),
        renderTicks: PERF_COUNTERS.renderTicks,
        filteredImageRenders: PERF_COUNTERS.filteredImageRenders,
        videoFrameUpdates: PERF_COUNTERS.videoFrameUpdates,
      });

      if (PREVIEW_TRACE.enabled) {
        const previewTrace = buildPreviewTraceSnapshot();
        if (previewTrace.count > 0) {
          studioDebug('[PREVIEW TRACE]', previewTrace);
        }
      }
    }

    // Reset counters
    PERF_COUNTERS.buildKonvaFilters = 0;
    PERF_COUNTERS.cacheOperations = 0;
    PERF_COUNTERS.batchDrawCalls = 0;
    PERF_COUNTERS.cacheDurations = [];
    PERF_COUNTERS.renderTicks = 0;
    PERF_COUNTERS.filteredImageRenders = 0;
    PERF_COUNTERS.filterComputeDurations = [];
    PERF_COUNTERS.drawDurations = [];
    PERF_COUNTERS.renderTickDurations = [];
    PERF_COUNTERS.videoFrameUpdates = 0;
    PERF_COUNTERS.longTasks = 0;
    PERF_COUNTERS.frameTimestamps = [];
    PERF_COUNTERS.lastResetTime = performance.now();
  }, 1000);
}

export function stopPerfHud(): void {
  if (perfHudIntervalId !== null) {
    clearInterval(perfHudIntervalId);
    perfHudIntervalId = null;
  }
}

// Filter function cache for memoization
const FILTER_FUNCTION_CACHE = new Map<string, Function>();

function getCachedFilterFunction(type: string, value: number): Function | null {
  const key = `${type}:${value}`;

  if (FILTER_FUNCTION_CACHE.has(key)) {
    return FILTER_FUNCTION_CACHE.get(key)!;
  }

  let fn: Function | null = null;

  switch (type) {
    case 'grain':
      fn = createGrainFilter(value);
      break;
    case 'vignette':
      fn = createVignetteFilter(value);
      break;
  }

  if (fn) {
    FILTER_FUNCTION_CACHE.set(key, fn);
  }

  return fn;
}

// Generate stable signature from filters + photoAdjustments
export function generateFilterSignature(filters: Filter[], photoAdjustments?: PhotoAdjustments, forceCPU?: boolean, lutSettings?: LUTSettings): string {
  const filterParts = filters.map(f => `${f.type}:${f.value}`).join(',');

  const adjParts: string[] = [];
  // Include basic adjustment values in signature for cache correctness
  if (photoAdjustments) {
    const { brightness = 0, contrast = 0, saturation = 0, exposure = 0,
            highlights = 0, shadows = 0, temperature = 0, hue = 0,
            tint = 0, vibrance = 0 } = photoAdjustments;
    const base = `b${brightness}c${contrast}s${saturation}e${exposure}h${highlights}sh${shadows}t${temperature}hu${hue}ti${tint}v${vibrance}`;
    if (base !== 'b0c0s0e0h0sh0t0hu0ti0v0') adjParts.push(base);
  }
  if (photoAdjustments?.hslChannels) {
    const hsl = photoAdjustments.hslChannels;
    const redHsl = hsl.red;
    if (redHsl) {
      adjParts.push(`hsl:${redHsl.hue ?? 0},${redHsl.saturation ?? 0},${redHsl.luminance ?? 0}`);
    }
  }
  if (photoAdjustments?.curves?.enabled && photoAdjustments.curves.channels) {
    const curves = photoAdjustments.curves.channels;
    const rPoints = curves.r?.points.map(p => `${p.x},${p.y}`).join(';') ?? '';
    const gPoints = curves.g?.points.map(p => `${p.x},${p.y}`).join(';') ?? '';
    const bPoints = curves.b?.points.map(p => `${p.x},${p.y}`).join(';') ?? '';
    adjParts.push(`curves:r[${rPoints}]g[${gPoints}]b[${bPoints}]`);
  }

  const adjString = adjParts.length > 0 ? `|${adjParts.join('|')}` : '';
  const cpuFlag = forceCPU ? '|cpu' : '';
  const lutFlag = lutSettings?.enabled && lutSettings.lut
    ? `|lut:${lutSettings.lut.title}:${lutSettings.lut.size}:${lutSettings.intensity}`
    : '';

  return `${filterParts}${adjString}${cpuFlag}${lutFlag}`;
}

// Cache for buildKonvaFilters results
const BUILD_KONVA_FILTERS_CACHE = new Map<string, {
  konvaFilters: KonvaFilter[],
  props: Record<string, number>,
  gpuFilters: { grain?: number, vignette?: number }
}>();

// Helper to build Konva filter array and properties from our filter format
export function buildKonvaFilters(filters: Filter[], photoAdjustments?: PhotoAdjustments, forceCPU?: boolean, lutSettings?: LUTSettings): {
  konvaFilters: KonvaFilter[],
  props: Record<string, number>,
  gpuFilters: { grain?: number, vignette?: number }
} {
  performance.mark('buildKonvaFilters-start');
  const buildStart = performance.now();

  // Check cache first
  const signature = generateFilterSignature(filters, photoAdjustments, forceCPU, lutSettings);
  const cached = BUILD_KONVA_FILTERS_CACHE.get(signature);

  if (cached) {
    // Cache hit - return memoized result without rebuilding
    performance.mark('buildKonvaFilters-end');
    performance.measure('buildKonvaFilters:cache-hit', 'buildKonvaFilters-start', 'buildKonvaFilters-end');
    return cached;
  }

  // Cache miss - build filters and store in cache
  PERF_COUNTERS.buildKonvaFilters++;

  const konvaFilters: KonvaFilter[] = [];
  const props: Record<string, number> = {};
  const gpuFilters: { grain?: number, vignette?: number } = {};

  const list: any[] = Array.isArray(filters) ? (filters as any[]) : [];
  list.forEach((f: any) => {
    if (!f || typeof f !== "object") return;
    switch (f.type) {
      case 'blur':
        if (f.value > 0) {
          konvaFilters.push(Blur);
          props.blurRadius = f.value;
        }
        break;
      case 'brightness':
        // Konva expects -1 to 1 range, we have -100 to 100
        konvaFilters.push(Brighten);
        props.brightness = f.value / 100;
        break;
      case 'contrast':
        // Konva expects -100 to 100 range (same as ours)
        konvaFilters.push(Contrast);
        props.contrast = f.value;
        break;
      case 'saturation':
        // Konva HSL expects -1 to 1 range, we have -100 to 100
        konvaFilters.push(HSL);
        props.saturation = f.value / 100;
        break;
      case 'grayscale':
        // Apply grayscale by desaturating
        if (f.value > 0) {
          konvaFilters.push(Grayscale);
        }
        break;
      case 'sepia':
        // Apply sepia filter
        if (f.value > 0) {
          konvaFilters.push(Sepia);
        }
        break;
      case 'invert':
        // Apply invert filter
        if (f.value > 0) {
          konvaFilters.push(Invert);
        }
        break;
      case 'hue-rotate':
        // Konva HSL hue expects 0 to 360
        if (f.value !== 0) {
          konvaFilters.push(HSL);
          props.hue = f.value;
        }
        break;
      case 'warm':
        // Warm: Add orange/yellow tint via hue shift and slight saturation boost
        // value is 0-100, shift hue towards orange (30-50 degrees)
        if (f.value > 0) {
          konvaFilters.push(HSL);
          // Positive hue shift towards warm colors
          props.hue = (props.hue || 0) + f.value * 0.3; // Max ~30 degree shift
          props.saturation = (props.saturation || 0) + f.value * 0.002; // Slight saturation boost
        }
        break;
      case 'cool':
        // Cool: Add blue tint via hue shift
        // value is 0-100, shift hue towards blue (180-210 degrees)
        if (f.value > 0) {
          konvaFilters.push(HSL);
          // Negative hue shift towards cool colors
          props.hue = (props.hue || 0) - f.value * 0.3; // Max ~30 degree shift
          props.saturation = (props.saturation || 0) - f.value * 0.001; // Slight desaturation
        }
        break;
      case 'grain':
        // Film grain: use GPU overlay in preview; CPU filter only when forceCPU is set
        if (f.value > 0) {
          if (forceCPU) {
            // Export quality: use cached CPU filter
            const grainFilter = getCachedFilterFunction('grain', f.value);
            if (grainFilter) konvaFilters.push(grainFilter as any);
          } else {
            // Preview performance: skip CPU filter, use overlay instead
            gpuFilters.grain = f.value;
          }
        }
        break;
      case 'vignette':
        // Vignette: use GPU overlay in preview; CPU filter only when forceCPU is set
        if (f.value > 0) {
          if (forceCPU) {
            // Export quality: use cached CPU filter
            const vignetteFilter = getCachedFilterFunction('vignette', f.value);
            if (vignetteFilter) konvaFilters.push(vignetteFilter as any);
          } else {
            // Preview performance: skip CPU filter, use overlay instead
            gpuFilters.vignette = f.value;
          }
        }
        break;
    }
  });

  // Add custom HSL per-channel and curves filter if photoAdjustments has them
  if (photoAdjustments) {
    const customFilter = createHslCurvesFilter({
      hslChannels: photoAdjustments.hslChannels,
      curves: photoAdjustments.curves,
    });
    if (customFilter) {
      // Konva filters receive imageData as the first argument
      // Cast to any to satisfy the KonvaFilter type signature
      konvaFilters.push(customFilter as any);
    }
  }

  // Add LUT color grading filter if active
  if (lutSettings?.enabled && lutSettings.lut) {
    const lutFilter = createLUTFilter(lutSettings);
    if (lutFilter) {
      konvaFilters.push(lutFilter as any);
    }
  }

  // Cache the result before returning
  const result = { konvaFilters, props, gpuFilters };
  BUILD_KONVA_FILTERS_CACHE.set(signature, result);

  const buildDuration = performance.now() - buildStart;
  PERF_COUNTERS.filterComputeDurations.push(buildDuration);

  if (buildDuration > 50) {
    PERF_COUNTERS.longTasks++;
    if (isFilterPerfDebugEnabled()) {
      console.warn(`[FilterPerf] buildKonvaFilters() took ${buildDuration.toFixed(2)}ms`);
    }
  }

  performance.mark('buildKonvaFilters-end');
  performance.measure('buildKonvaFilters:cache-miss', 'buildKonvaFilters-start', 'buildKonvaFilters-end');

  return result;
}

// Helper: Get points for simple arrow
